import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PLATFORM_COLORS } from "../lib/constants";
import { Crosshair, TrendingUp, ChevronRight } from "lucide-react";

// This week's crowd-nominated "Most Wanted" — top community suspects by net upvotes.
export const MostWanted = ({ onOpen }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/most-wanted")
      .then(({ data }) => setList(data || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || list.length === 0) return null;

  const [lead, ...rest] = list;
  const color = (p) => PLATFORM_COLORS[p] || "#00F0FF";

  return (
    <section data-testid="most-wanted" className="mx-auto max-w-7xl px-4 pt-10 sm:px-6">
      <div className="relative overflow-hidden rounded-2xl border border-[#F97316]/30 bg-gradient-to-br from-[#1A0F08] via-[#0F1424] to-[#0F1424] p-6 sm:p-8">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#F97316]/10 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#F97316]/40 bg-[#F97316]/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#F97316]">
              <Crosshair size={13} /> Most Wanted
            </span>
            <span className="text-xs font-semibold text-[#94A3B8]">Community's top suspects this week</span>
          </div>
          <h2 className="mt-3 font-heading text-2xl font-black tracking-tight text-white sm:text-3xl">
            The lobbies want these players <span className="text-[#F97316]">off the court.</span>
          </h2>
          <p className="mt-1.5 max-w-xl text-sm text-[#94A3B8]">
            Ranked by upvotes gained since Monday. Awaiting a mod verdict — upvote if you've run into them too. Resets weekly.
          </p>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* #1 spotlight */}
            <button
              data-testid="most-wanted-lead"
              onClick={() => onOpen?.(lead)}
              className="group relative flex flex-col justify-between overflow-hidden rounded-xl border border-[#F97316]/40 bg-[#F97316]/[0.07] p-5 text-left transition-all hover:border-[#F97316] lg:row-span-1"
            >
              <div className="flex items-start justify-between">
                <span className="font-heading text-5xl font-black leading-none text-[#F97316]/70">#1</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-[#F97316]/20 px-2 py-1 text-xs font-bold text-[#F97316]">
                  <TrendingUp size={12} /> +{lead.weekly_score} this week
                </span>
              </div>
              <div className="mt-6">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color(lead.platform) }} />
                  <span className="text-xs font-bold uppercase tracking-wide" style={{ color: color(lead.platform) }}>{lead.platform}</span>
                </div>
                <div className="mt-1 truncate font-heading text-2xl font-black text-white">{lead.tracked_name || lead.gamertag}</div>
                <div className="mt-1 text-sm text-[#94A3B8]">{lead.cheat_type}</div>
                {(() => { const need = Math.max(0, (lead.hot_threshold || 10) - (lead.score || 0)); return (
                  <div className="mt-2 text-xs font-semibold" data-testid="most-wanted-lead-countdown">
                    {lead.hot ? (
                      <span className="text-[#F97316]">🔥 Hot Suspect — mods are on it</span>
                    ) : (
                      <span className="text-[#FDBA74]">{need} more upvote{need === 1 ? "" : "s"} to hit 🔥 Hot</span>
                    )}
                  </div>
                ); })()}
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#F97316] opacity-0 transition-opacity group-hover:opacity-100">
                  View case <ChevronRight size={13} />
                </span>
              </div>
            </button>

            {/* #2–#5 list */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {rest.map((r, i) => (
                  <button
                    key={r.id}
                    data-testid={`most-wanted-item-${i}`}
                    onClick={() => onOpen?.(r)}
                    className="group flex items-center gap-3 rounded-xl border border-[#1E293F] bg-[#141B2D] p-3 text-left transition-all hover:border-[#F97316]/50 hover:bg-[#1A233A]"
                  >
                    <span className="font-heading text-xl font-black text-[#64748B] group-hover:text-[#F97316]">#{i + 2}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-heading text-sm font-bold text-white">{r.tracked_name || r.gamertag}</div>
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className="font-semibold" style={{ color: color(r.platform) }}>{r.platform}</span>
                        <span className="text-[#64748B]">·</span>
                        <span className="truncate text-[#94A3B8]">{r.cheat_type}</span>
                      </div>
                      {(() => { const need = Math.max(0, (r.hot_threshold || 10) - (r.score || 0)); return (
                        <div className="mt-0.5 text-[11px] font-semibold" data-testid={`most-wanted-countdown-${i}`}>
                          {r.hot ? <span className="text-[#F97316]">🔥 Hot Suspect</span> : <span className="text-[#FDBA74]">{need} more to hit Hot</span>}
                        </div>
                      ); })()}
                    </div>
                    <span className="shrink-0 rounded-full bg-[#F97316]/15 px-2 py-0.5 text-xs font-bold text-[#F97316]">+{r.weekly_score}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
